import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { apiClient, API, formatApiErrorDetail } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Download, LogOut, Loader2, RefreshCw, Trash2, AlertCircle } from "lucide-react";
import {
  MD_COUNTY_ELA_2025,
  MD_STATEWIDE_AVG_PCT,
  MD_ELA_SOURCE_LONG,
} from "@/lib/marylandEla";
import {
  VA_SOL_READING_2025,
  VA_STATEWIDE_PCT,
  VA_SOL_SOURCE_LONG,
} from "@/lib/virginiaSol";

const STATUS_LABEL = { pending: "Pending", scheduled: "Scheduled", active: "Installed" };

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    scheduled: "bg-slate-100 text-slate-700 border-slate-200",
    active: "bg-green-50 text-green-700 border-green-200",
  };
  return (
    <Badge variant="outline" className={`${map[status]} rounded-full font-medium`}>
      {STATUS_LABEL[status] || status}
    </Badge>
  );
}

function MetricCard({ label, value, accent, testId }) {
  return (
    <Card className="bg-white rounded-xl border border-stone-200 shadow-sm p-5 flex flex-col gap-2">
      <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{label}</span>
      <span
        data-testid={testId}
        className="font-serif text-3xl text-stone-900 tabular-nums"
        style={accent ? { color: accent } : undefined}
      >
        {(value || 0).toLocaleString()}
      </span>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [filters, setFilters] = useState({ status: "ALL", jurisdiction: "ALL", search: "", source: "ALL" });

  // Redirect when auth resolved as anonymous
  useEffect(() => {
    if (user === false) nav("/admin/login");
  }, [user, nav]);

  const params = useMemo(() => {
    const p = {};
    if (filters.status !== "ALL") p.status = filters.status;
    if (filters.jurisdiction !== "ALL") p.jurisdiction = filters.jurisdiction;
    if (filters.search.trim()) p.search = filters.search.trim();
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const [fRes, sRes] = await Promise.all([
        apiClient.get("/families/admin", { params }),
        apiClient.get("/stats/public"),
      ]);
      setRows(fRes.data || []);
      setStats(sRes.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [params, user]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const updateRowStatus = async (id, status) => {
    try {
      const body = { status };
      await apiClient.patch(`/families/${id}/status`, body);
      toast.success(`Marked as ${STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const applyBulk = async () => {
    if (!bulkStatus || selected.size === 0) {
      toast.message("Pick a status and at least one family.");
      return;
    }
    try {
      const { data } = await apiClient.post("/families/bulk-status", {
        ids: Array.from(selected),
        status: bulkStatus,
      });
      toast.success(`Updated ${data.updated} families`);
      setSelected(new Set());
      setBulkStatus("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await apiClient.get("/families/admin/export.csv", {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookshelf-families-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const deleteRow = async (r) => {
    const ok = window.confirm(
      `Delete this family permanently?\n\n${r.first_name} ${r.last_name}\n${r.neighborhood || r.jurisdiction}\nStatus: ${r.status}\n\nThis cannot be undone. If the family came from the portal (external_id present) it will reappear on the next portal sync.`
    );
    if (!ok) return;
    try {
      await apiClient.delete(`/families/${r.id}`);
      toast.success(`Deleted ${r.first_name} ${r.last_name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Filter rows client-side by "source" (portal vs direct) so the admin can
  // quickly find orphan / test records that don't have an external_id.
  const visibleRows = rows.filter((r) => {
    if (filters.source === "PORTAL") return !!r.external_id;
    if (filters.source === "DIRECT") return !r.external_id;
    return true;
  });
  const orphanCount = rows.filter((r) => !r.external_id).length;

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-stone-500" />
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col">
      <SiteHeader />
      <section className="max-w-7xl mx-auto w-full px-6 lg:px-8 pt-12 pb-20 flex-1">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Admin · Delivery operations</div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-2">Family management</h1>
            <p className="text-sm text-stone-600 mt-1">
              Signed in as <span className="font-medium text-stone-800">{user?.email}</span>
            </p>
          </div>
          <Button
            data-testid={ADMIN.logoutButton}
            variant="outline"
            onClick={async () => { await logout(); nav("/admin/login"); }}
            className="rounded-full border-stone-200"
          >
            <LogOut size={14} className="mr-1.5" />
            Sign out
          </Button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard label="Total registered" value={stats?.total_registered} testId={ADMIN.metricRegistered} />
          <MetricCard label="Installed" value={stats?.total_installed} accent="#16A34A" testId={ADMIN.metricInstalled} />
          <MetricCard label="Pending deliveries" value={stats?.pending_deliveries} accent="#B45309" testId={ADMIN.metricPending} />
          <MetricCard label="New this month" value={stats?.new_this_month} testId={ADMIN.metricNewMonth} />
        </div>

        {/* Toolbar */}
        <Card className="bg-white border border-stone-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              data-testid={ADMIN.searchInput}
              placeholder="Search name, neighborhood, address…"
              value={filters.search}
              onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))}
              className="rounded-xl max-w-xs"
            />
            <Select value={filters.status} onValueChange={(v) => setFilters((s) => ({ ...s, status: v }))}>
              <SelectTrigger data-testid={ADMIN.filterStatus} className="rounded-xl w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="active">Installed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.jurisdiction} onValueChange={(v) => setFilters((s) => ({ ...s, jurisdiction: v }))}>
              <SelectTrigger data-testid={ADMIN.filterJurisdiction} className="rounded-xl w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All regions</SelectItem>
                <SelectItem value="DC">DC</SelectItem>
                <SelectItem value="MD">Maryland</SelectItem>
                <SelectItem value="VA">Virginia</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.source} onValueChange={(v) => setFilters((s) => ({ ...s, source: v }))}>
              <SelectTrigger data-testid="admin-filter-source" className="rounded-xl w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All sources</SelectItem>
                <SelectItem value="PORTAL">Portal (external_id)</SelectItem>
                <SelectItem value="DIRECT">Direct / orphan ({orphanCount})</SelectItem>
              </SelectContent>
            </Select>

            <div className="grow" />

            <Button
              variant="outline"
              onClick={load}
              className="rounded-full border-stone-200"
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button
              data-testid={ADMIN.exportCsvButton}
              variant="outline"
              onClick={exportCsv}
              className="rounded-full border-stone-200"
            >
              <Download size={14} className="mr-1.5" /> Export CSV
            </Button>
          </div>

          {/* Bulk row */}
          <div className="flex items-center gap-3 flex-wrap mt-4 pt-4 border-t border-stone-100">
            <span className="text-sm text-stone-600">
              {selected.size} selected
            </span>
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger data-testid={ADMIN.bulkStatusSelect} className="rounded-xl w-[200px]">
                <SelectValue placeholder="Bulk: set status to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="active">Installed</SelectItem>
              </SelectContent>
            </Select>
            <Button
              data-testid={ADMIN.bulkStatusApply}
              onClick={applyBulk}
              disabled={selected.size === 0 || !bulkStatus}
              className="bg-[#122036] hover:bg-[#0B1626] text-white rounded-full px-5"
            >
              Apply
            </Button>
          </div>
        </Card>

        {/* Table */}
        <Card data-testid={ADMIN.familyTable} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/70">
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid={ADMIN.selectAllCheckbox}
                      checked={rows.length > 0 && selected.size === rows.length}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Family</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Address</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Neighborhood</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Region</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Source</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500 text-right">Children</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Status</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500">Update</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.14em] text-stone-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-10 text-stone-500">
                      <Loader2 className="animate-spin inline mr-2" size={16} /> Loading families…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && visibleRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12 text-stone-500 text-sm">
                      No families match these filters. As families register, they'll appear here.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && visibleRows.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid={ADMIN.familyRow}
                    className={`hover:bg-stone-50/50 ${!r.external_id ? "bg-amber-50/30" : ""}`}
                  >
                    <TableCell>
                      <Checkbox
                        data-testid={ADMIN.rowCheckbox}
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggleSelect(r.id)}
                        aria-label={`Select ${r.first_name} ${r.last_name}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-stone-900">{r.first_name} {r.last_name}</div>
                      <div className="text-xs text-stone-500">{r.email || r.phone || "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm text-stone-700 max-w-[220px] truncate" title={r.address}>{r.address}</TableCell>
                    <TableCell className="text-sm">{r.neighborhood}{r.ward ? ` · Ward ${r.ward}` : ""}</TableCell>
                    <TableCell className="text-sm">{r.jurisdiction}</TableCell>
                    <TableCell className="text-xs">
                      {r.external_id ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100" title={r.external_id}>
                          Portal · <span className="font-mono">{r.external_id.length > 14 ? r.external_id.slice(0, 12) + "…" : r.external_id}</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100" title="No external_id — this row was created directly via the API or admin tooling, not the Family Portal.">
                          <AlertCircle size={11} /> Direct / orphan
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.num_children}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => updateRowStatus(r.id, v)}>
                        <SelectTrigger data-testid={ADMIN.statusSelect} className="rounded-lg w-[140px] h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="active">Installed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRow(r)}
                        data-testid="admin-row-delete-btn"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 px-2"
                        aria-label={`Delete ${r.first_name} ${r.last_name}`}
                      >
                        <Trash2 size={15} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Maryland MCAP reference data — for delivery operations / grant context */}
        <Card className="bg-white border border-stone-200 rounded-2xl p-5 mt-6">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Reference data</div>
              <h2 className="font-serif text-xl mt-1">Maryland ELA Proficiency by County</h2>
              <p className="text-[12px] text-stone-500 mt-0.5">
                Source: {MD_ELA_SOURCE_LONG} · Statewide average: {MD_STATEWIDE_AVG_PCT}%
              </p>
            </div>
            <div className="text-[11px] text-stone-500 max-w-md text-right">
              Update each September — pull the latest LEA-level table from
              <span className="whitespace-nowrap"> marylandpublicschools.org → Assessment → MCAP Results </span>
              and edit <code className="bg-stone-100 px-1.5 py-0.5 rounded text-[10px]">src/lib/marylandEla.js</code>.
            </div>
          </div>
          <div className="overflow-x-auto" data-testid="admin-md-county-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>County</TableHead>
                  <TableHead>FIPS</TableHead>
                  <TableHead className="text-right">ELA Proficiency</TableHead>
                  <TableHead className="text-right">vs. State Avg</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(MD_COUNTY_ELA_2025)
                  .sort(([, a], [, b]) => a.ela_pct - b.ela_pct)
                  .map(([fips, c]) => {
                    const delta = c.ela_pct - MD_STATEWIDE_AVG_PCT;
                    const deltaColor =
                      delta >= 5 ? "text-emerald-700"
                        : delta <= -5 ? "text-red-700"
                        : "text-stone-500";
                    const sign = delta > 0 ? "+" : "";
                    return (
                      <TableRow key={fips}>
                        <TableCell className="font-medium">
                          {c.is_city ? c.name : `${c.name} County`}
                        </TableCell>
                        <TableCell className="text-stone-500 text-[12px]">{fips}</TableCell>
                        <TableCell className="text-right font-mono text-[13px]">
                          {c.ela_pct.toFixed(1)}%
                        </TableCell>
                        <TableCell className={`text-right font-mono text-[13px] ${deltaColor}`}>
                          {sign}{delta.toFixed(1)} pts
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Virginia SOL reference data */}
        <Card className="bg-white border border-stone-200 rounded-2xl p-5 mt-6">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Reference data</div>
              <h2 className="font-serif text-xl mt-1">Virginia SOL Reading by Division</h2>
              <p className="text-[12px] text-stone-500 mt-0.5">
                Source: {VA_SOL_SOURCE_LONG} · Statewide pass rate: {VA_STATEWIDE_PCT}%
              </p>
            </div>
            <div className="text-[11px] text-stone-500 max-w-md text-right">
              Update each fall — VDOE releases SOL results in late August. Pull the latest
              from doe.virginia.gov build-a-table (Year, Subject = English Reading, All Students,
              Level = Division) and edit <code className="bg-stone-100 px-1.5 py-0.5 rounded text-[10px]">src/lib/virginiaSol.js</code>.
            </div>
          </div>
          <div className="overflow-x-auto" data-testid="admin-va-division-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Division</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>FIPS</TableHead>
                  <TableHead className="text-right">SOL Reading</TableHead>
                  <TableHead className="text-right">vs. State Avg</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(VA_SOL_READING_2025)
                  .sort(([, a], [, b]) => a.read_pct - b.read_pct)
                  .map(([fips, c]) => {
                    const delta = c.read_pct - VA_STATEWIDE_PCT;
                    const deltaColor =
                      delta >= 5 ? "text-emerald-700"
                        : delta <= -5 ? "text-red-700"
                        : "text-stone-500";
                    const sign = delta > 0 ? "+" : "";
                    return (
                      <TableRow key={fips}>
                        <TableCell className="font-medium">{c.division}</TableCell>
                        <TableCell className="text-stone-600 text-[12px]">{c.region}</TableCell>
                        <TableCell className="text-stone-500 text-[12px]">{fips}</TableCell>
                        <TableCell className="text-right font-mono text-[13px]">{c.read_pct}%</TableCell>
                        <TableCell className={`text-right font-mono text-[13px] ${deltaColor}`}>
                          {sign}{delta} pts
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>
      <SiteFooter />
    </div>
  );
}
